import { inject, Injectable, type Signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { createActionResponseFactory, type ID } from '@core/ngrx-legacy';
import { generateUuid } from '@core/utils-legacy';
import { type ViewCounter, ViewCountersActions, ViewCountersSelectors } from '@core/views-data';
import type { DynamicFormConfig } from '@tellent-ask-hr/dynamic-forms-types';
import { AskTicketsActions, AskTicketsSelectors } from '@tellent-ask-hr/tickets-data';
import type {
  AskHrTicket,
  CreateAskTicket,
  MoveAskTicket,
  UpdateAskHrTicket,
} from '@tellent-ask-hr/tickets-types';

@Injectable({ providedIn: 'root' })
export class TicketsDataService {
  private readonly store = inject(Store);
  private readonly ticketsActionResponse = createActionResponseFactory(AskTicketsActions);

  public counters = this.store.selectSignal(
    ViewCountersSelectors.entities({ scope: 'ask_tickets' }),
  );

  public loadCounters(viewIds: ID[]) {
    this.store.dispatch(
      ViewCountersActions.fetchCollection({
        payload: { dataSourceId: 'ask_tickets', ids: viewIds },
      }),
    );
  }

  public getCounter(viewId: ID): Signal<ViewCounter | null> {
    return this.store.selectSignal(ViewCountersSelectors.byId(viewId));
  }

  public createTicket(template: CreateAskTicket<DynamicFormConfig>) {
    return this.ticketsActionResponse(
      AskTicketsActions.createOnePessimistic,
      { payload: { data: template, correlationId: generateUuid() } },
      {
        success: AskTicketsActions.createOnePessimisticSuccess,
        failed: AskTicketsActions.createOnePessimisticFailed,
      },
    );
  }

  public loadTicketById(id: number) {
    return this.ticketsActionResponse(
      AskTicketsActions.fetchOne,
      { payload: { id } },
      {
        success: AskTicketsActions.fetchOneSuccess,
        failed: AskTicketsActions.fetchOneFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTicketsSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTicketsSelectors.error(payload.id)),
      },
    );
  }

  public updateTicket(id: number, ticket: UpdateAskHrTicket) {
    return this.ticketsActionResponse(
      AskTicketsActions.updateOnePessimistic,
      { payload: { id, data: ticket } },
      {
        success: AskTicketsActions.updateOnePessimisticSuccess,
        failed: AskTicketsActions.updateOnePessimisticFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTicketsSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTicketsSelectors.error(payload.data.id)),
      },
    );
  }

  public moveTicket(id: number, ticket: MoveAskTicket) {
    return this.ticketsActionResponse(
      AskTicketsActions.move,
      { payload: { id, data: ticket } },
      {
        success: AskTicketsActions.moveSuccess,
        failed: AskTicketsActions.moveFailed,
      },
    );
  }

  public cancelTicket(id: number) {
    return this.ticketsActionResponse(
      AskTicketsActions.cancel,
      { payload: { id } },
      {
        success: AskTicketsActions.cancelSuccess,
        failed: AskTicketsActions.cancelFailed,
      },
    );
  }

  public getTicketById(id: number): Signal<AskHrTicket | null> {
    return this.store.selectSignal(AskTicketsSelectors.byId(id));
  }
}
